import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { CardHeader } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import {
	getRequestModes,
	getTimesheetFilters,
	resolveRequestType,
	resolveTimesheetFilter,
} from "./permissions";

const config = {
    pto: [],
    clock: ["all", "shift", "edit-shift"],
    schedule: [
        "all",
        "schedule",
        "edit-schedule",
        "submit-schedule",
        "swap-shifts",
        "offer-shifts",
    ],
    availability: ["all", "recurring", "temporary"],
    timesheet: ["all", "meal-waive", "miss-punch"],
    faceRecognition: ["all", "face-enrollment", "face-mismatch"],
};


function Topbar({
	namespace,
	timeoff = false,
	filterType,
	setFilterType,
	setSearchParams,
	setHasChanges,
	requestType,
	accessMap
}) {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const modes = useMemo(
		() => getRequestModes(accessMap, namespace),
		[accessMap, namespace],
	);
	const resolvedRequestType = useMemo(
		() => resolveRequestType(accessMap, namespace, requestType),
		[accessMap, namespace, requestType],
	);

	const filterOptions = useMemo(() => {
		if (namespace === "timesheet" && resolvedRequestType) {
			return getTimesheetFilters(accessMap, resolvedRequestType);
		}
		return config?.[namespace] || [];
	}, [accessMap, namespace, resolvedRequestType]);

    const btnText = useMemo(() => {
        switch (namespace) {
            case "clock": {
                return t("my-clock-request")
            }
            case "pto": {
                return t("my-pto-request")
            }
            case "schedule": {
                return t("my-schedule-request")
            }
            case "availability": {
                return t("availability")
            }
            case "timesheet":
                return t("timesheet-request")
            case "faceRecognition":
                return t("my-face-recognition-request")
            default:
                return ""
        }
    }, [namespace, t]);

    const teamBtnText = useMemo(() => {
        switch (namespace) {
            case "clock": {
                return t("team-clock-request")
            }
            case "pto": {
                return t("team-pto-request")
            }
            case "schedule": {
                return t("team-schedule-request")
            }
            case "availability": {
                return t("team-availability")
            }
            case "timesheet":
                return t("team-timesheet-request")
            case "faceRecognition":
                return t("team-face-recognition-request")
            default:
                return ""
        }
    }, [namespace, t]);

	const onRequestTypeChange = useCallback((value) => {
		if (!modes?.[value]) {
			return;
		}
		const nextFilter = namespace === "timesheet"
			? resolveTimesheetFilter(accessMap, value, "all")
			: "all";

		setFilterType(nextFilter || "all");
		setSearchParams((prev) => {
			prev.set("requestType", value);
			return prev;
		});
	}, [modes, namespace, accessMap, setFilterType, setSearchParams]);

    const onFilterTypeChange = useCallback((value) => {
        setFilterType(value);
        setHasChanges(true);
    }, [setFilterType, setHasChanges]);

	useEffect(() => {
		const id = searchParams.get("id");
		if (!resolvedRequestType && !id) {
			if (modes.my) {
				onRequestTypeChange("my");
			} else if (modes.team) {
				onRequestTypeChange("team");
			}
		}
	}, [resolvedRequestType, searchParams, modes, onRequestTypeChange]);

	if (timeoff || (!modes.my && !modes.team)) {
		return null;
	}

	return (
		<CardHeader className="d-flex align-items-center justify-content-between flex-wrap">
			<div className="d-flex align-items-center gap-2 my-2">
				{modes.my && (
					<span
						color="muted"
						className={classNames(
							(resolvedRequestType || "my") === "my"
								? "bg-primary text-white"
								: "text-primary",
							"py-1 px-4 text-sm rounded border border-primary cursor-pointer"
						)}
						onClick={() => onRequestTypeChange("my")}
					>
						{btnText}
					</span>
				)}

				{modes.team && (
					<span
						color="muted"
						className={classNames(
							resolvedRequestType === "team"
								? "bg-primary text-white"
								: "text-primary",
							"py-1 px-4 text-sm rounded border-primary border cursor-pointer"
						)}
						onClick={() => onRequestTypeChange("team")}
					>
						{teamBtnText}
					</span>
				)}
			</div>

			<div>
				{filterOptions?.map((item) => (
					<span
						key={item}
						className={classNames(
							filterType === item && "bg-primary text-white",
							"px-4 py-1 rounded text-sm cursor-pointer"
						)}
						onClick={() => {
							let next = item;
							if (namespace === "timesheet") {
								next = resolveTimesheetFilter(accessMap, resolvedRequestType, item);
								if (!next) {
									return;
								}
							}
							onFilterTypeChange(next);
						}}
					>
						{t(item)}
					</span>
				))}
			</div>
		</CardHeader>
	);
}

export default Topbar;
